import "core-js/modules/es.array.index-of.js";
import PropTypes from "prop-types";
import "core-js/modules/es.string.ends-with.js";
/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { PureComponent } from 'react';
import classNames from 'classnames';
import { keysOf } from '../common';
import { typeToPathMap } from './icon_map';
import { icon as empty } from './assets/empty';
import { enqueueStateChange } from '../../services/react';
import { htmlIdGenerator, withEuiTheme } from '../../services';
export { COLORS } from './named_colors';
import { isNamedColor } from './named_colors';
import { euiIconStyles } from './icon.styles';
var getIsAppIcon = function getIsAppIcon(iconType) {
  if (typeof iconType !== 'string') return false;
  if (iconType === 'dataVisualizer') return true; // Special case
  if (iconType.indexOf('data:') === 0) return false; // Inline data URIs should be short-circuited for performance
  return iconType.endsWith('App') || iconType.endsWith('Job');
};
export var TYPES = keysOf(typeToPathMap);
export var SIZES = ['original', 's', 'm', 'l', 'xl', 'xxl'];
function isEuiIconType(x) {
  return typeof x === 'string' && typeToPathMap.hasOwnProperty(x);
}
function getInitialIcon(icon) {
  if (icon == null) {
    return undefined;
  }
  if (isEuiIconType(icon)) {
    if (iconComponentCache.hasOwnProperty(icon)) {
      return iconComponentCache[icon];
    }
    return undefined;
  }
  return icon;
}
var generateId = htmlIdGenerator();
var iconComponentCache = {};
export var clearIconComponentCache = function clearIconComponentCache(iconType) {
  if (iconType != null) {
    delete iconComponentCache[iconType];
  } else {
    iconComponentCache = {};
  }
};
export var appendIconComponentCache = function appendIconComponentCache(iconTypeToIconComponentMap) {
  for (var _iconType in iconTypeToIconComponentMap) {
    if (iconTypeToIconComponentMap.hasOwnProperty(_iconType)) {
      iconComponentCache[_iconType] = iconTypeToIconComponentMap[_iconType];
    }
  }
};
export class EuiIconClass extends PureComponent {
  isMounted = false;
  constructor(props) {
    super(props);
    const {
      type
    } = props;
    const initialIcon = getInitialIcon(type);
    this.state = {
      icon: initialIcon,
      iconTitle: undefined,
      isLoading: false,
      neededLoading: false
    };
  }
  componentDidMount() {
    this.isMounted = true;
    const {
      type
    } = this.props;
    if (isEuiIconType(type) && this.state.icon == null) {
      //eslint-disable-next-line react/no-did-mount-set-state
      this.setState({
        neededLoading: true,
        isLoading: true
      });
      this.loadIconComponent(type);
    } else {
      this.onIconLoad();
    }
  }
  componentDidUpdate(prevProps) {
    const {
      type
    } = this.props;
    if (type !== prevProps.type) {
      if (isEuiIconType(type)) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({
          neededLoading: iconComponentCache.hasOwnProperty(type),
          isLoading: true
        });
        this.loadIconComponent(type);
      } else {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({
          icon: type,
          neededLoading: true,
          isLoading: false
        });
      }
    }
  }
  componentWillUnmount() {
    this.isMounted = false;
  }
  loadIconComponent = iconType => {
    if (iconComponentCache.hasOwnProperty(iconType)) {
      // exists in cache
      this.setState({
        isLoading: false,
        neededLoading: false,
        icon: iconComponentCache[iconType]
      });
      this.onIconLoad();
      return;
    }
    import( /* webpackChunkName: "icon.[request]" */
    // It's important that we don't use a template string here, it
    // stops webpack from building a dynamic require context.
    // eslint-disable-next-line prefer-template
    './assets/' + typeToPathMap[iconType]).then(({
      icon
    }) => {
      iconComponentCache[iconType] = icon;
      enqueueStateChange(() => {
        if (this.isMounted && this.props.type === iconType) {
          this.setState({
            icon,
            iconTitle: iconType,
            isLoading: false
          }, this.onIconLoad);
        }
      });
    });
  };
  onIconLoad = () => {
    const {
      onIconLoad
    } = this.props;
    if (onIconLoad) {
      onIconLoad();
    }
  };
  render() {
    const {
      type,
      size = 'm',
      color,
      className,
      tabIndex,
      title,
      onIconLoad,
      theme,
      style,
      ...rest
    } = this.props;
    const {
      isLoading,
      neededLoading,
      iconTitle
    } = this.state;
    const isLoaded = !isLoading && neededLoading;
    const isCustomColor = color && !isNamedColor(color);
    const optionalCustomStyles = isCustomColor ? {
      color: color,
      ...style
    } : style;

    // These icons are a little special and get some extra CSS flexibility
    const isAppIcon = getIsAppIcon(type);
    // App color styles are only applied if no color is passed or if color="default" is passed
    const appIconHasColor = color && color !== 'default';

    // The Elastic logo should be an outline in text and ghost mode
    const isElasticLogoOutline = type === 'logoElastic' && (color === 'ghost' || color === 'text');
    const classes = classNames('euiIcon', className);

    // Emotion styles
    const styles = euiIconStyles(theme);
    const cssStyles = [styles.euiIcon, styles[size], color && isNamedColor(color) && styles[color], isCustomColor && styles.customColor, isElasticLogoOutline && styles.logoElasticOutline, isAppIcon && !appIconHasColor && styles.app, isLoading && styles.isLoading, !isLoading && neededLoading && styles.isLoaded];
    const icon = this.state.icon || empty;
    if (typeof icon === 'string') {
      return <img alt={title ? title : ''} src={icon} className={classes} css={cssStyles} tabIndex={tabIndex} {...rest} />;
    } else {
      const Svg = icon;

      // If it's an empty icon, or if there is no aria-label, aria-labelledby, or title it gets aria-hidden true
      const isAriaHidden = icon === empty || !(this.props['aria-label'] || this.props['aria-labelledby'] || this.props.title);
      const hideIconEmpty = isAriaHidden && {
        'aria-hidden': true
      };
      let titleId;

      // If no aria-label or aria-labelledby is provided but there's a title, a titleId is generated
      //  The svg aria-labelledby attribute gets this titleId
      //  The svg title element gets this titleId as an id
      if (!this.props['aria-label'] && !this.props['aria-labelledby'] && title) {
        titleId = {
          titleId: generateId()
        };
      }
      return <Svg className={classes} style={optionalCustomStyles} css={cssStyles} tabIndex={tabIndex} role="img" title={title} data-icon-type={iconTitle} data-is-loaded={isLoaded || undefined} data-is-loading={isLoading || undefined} {...titleId} {...rest} {...hideIconEmpty} />;
    }
  }
}
EuiIconClass.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  /**
       * `Enum` is any of the named icons listed in the docs, `string` is usually a URL to an SVG file, and `elementType` is any React SVG component
       */
  type: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addDataApp", "advancedSettingsApp", "agentApp", "aggregate", "analyzeEvent", "annotation", "apmApp", "apmTrace", "appSearchApp", "apps", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "arrowStart", "arrowEnd", "article", "asterisk", "auditbeatApp", "beaker", "bell", "bellSlash", "beta", "bolt", "boxesHorizontal", "boxesVertical", "branch", "branchUser", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "casesApp", "check", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "cluster", "codeApp", "color", "compute", "console", "consoleApp", "container", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "currency", "cut", "dashboardApp", "dataVisualizer", "database", "desktop", "devToolsApp", "discoverApp", "discuss", "document", "documentEdit", "documentation", "documents", "dot", "dotInCircle", "doubleArrowLeft", "doubleArrowRight", "download", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorChecklist", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignLeft", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "eql", "eraser", "error", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "filterExclude", "filterIgnore", "filterInclude", "filterInCircle", "flag", "fleetApp", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "gear", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "indexTemporary", "infinity", "inputOutput", "inspect", "invert", "ip", "keyboard", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "kubernetesNode", "kubernetesPod", "launch", "layers", "lensApp", "lettering", "lineDashed", "lineDotted", "lineSolid", "link", "list", "listAdd", "lock", "lockOpen", "logoAWS", "logoAWSMono", "logoAerospike", "logoApache", "logoAppSearch", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticStack", "logoElasticsearch", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoVulnerabilityManagement", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logsApp", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithExclamation", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "namespace", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "paperClip", "partial", "pause", "payment", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "pivot", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "popout", "push", "questionInCircle", "quote", "recentlyViewedApp", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "sessionViewer", "shard", "share", "snowflake", "sortAscending", "sortDescending", "sortDown", "sortLeft", "sortRight", "sortUp", "sortable", "spaces", "spacesApp", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "sun", "swatchInput", "symlink", "tableDensityCompact", "tableDensityExpanded", "tableDensityNormal", "tableOfContents", "tag", "tear", "temperature", "timeline", "timelionApp", "timeRefresh", "timeslider", "training", "trash", "unfold", "unlink", "upgradeAssistantApp", "uptimeApp", "user", "userAvatar", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visTable", "visTagCloud", "visText", "visTimelion", "visVega", "visVisualBuilder", "visualizeApp", "vulnerabilityManagementApp", "warning", "alert", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenAlias", "tokenAnnotation", "tokenArray", "tokenBinary", "tokenBoolean", "tokenClass", "tokenCompletionSuggester", "tokenConstant", "tokenDate", "tokenDenseVector", "tokenElement", "tokenEnum", "tokenEnumMember", "tokenEvent", "tokenException", "tokenField", "tokenFile", "tokenFlattened", "tokenFunction", "tokenGeo", "tokenHistogram", "tokenInterface", "tokenIP", "tokenJoin", "tokenKey", "tokenKeyword", "tokenMethod", "tokenMetricCounter", "tokenMetricGauge", "tokenModule", "tokenNamespace", "tokenNested", "tokenNull", "tokenNumber", "tokenObject", "tokenOperator", "tokenPackage", "tokenParameter", "tokenPercolator", "tokenProperty", "tokenRange", "tokenRankFeature", "tokenRankFeatures", "tokenRepo", "tokenSearchType", "tokenShape", "tokenString", "tokenStruct", "tokenSymbol", "tokenTag", "tokenText", "tokenTokenCount", "tokenVariable"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]).isRequired,
  /**
       * One of EUI's color palette or a valid CSS color value https://developer.mozilla.org/en-US/docs/Web/CSS/color_value.
       * Note that coloring only works if your SVG is removed of fill attributes.
       */
  color: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.any.isRequired]),
  /**
       * Note that every size other than `original` assumes the provided SVG sits on a square viewbox.
       */
  size: PropTypes.any,
  /**
       * Descriptive title for naming the icon based on its use
       */
  title: PropTypes.string,
  /**
       * A unique identifier for the title element
       */
  titleId: PropTypes.string,
  /**
       * Its value should be one or more element IDs
       */
  "aria-labelledby": PropTypes.string,
  /**
       * Callback when the icon has been loaded & rendered
       */
  onIconLoad: PropTypes.func
};
export const EuiIcon = withEuiTheme(EuiIconClass);
try {
  clearIconComponentCache.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/icon/icon.tsx',
    description: '',
    displayName: 'clearIconComponentCache',
    methods: [],
    props: {},
    extendedInterfaces: ['String', 'CommonProps', 'SVGAttributes', 'AriaAttributes', 'DOMAttributes', 'WithEuiThemeProps', 'Attributes']
  };
} catch (e) {}
try {
  appendIconComponentCache.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/icon/icon.tsx',
    description: '',
    displayName: 'appendIconComponentCache',
    methods: [],
    props: {},
    extendedInterfaces: ['String', 'CommonProps', 'SVGAttributes', 'AriaAttributes', 'DOMAttributes', 'WithEuiThemeProps', 'Attributes']
  };
} catch (e) {}
try {
  EuiIconClass.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/icon/icon.tsx',
    description: '',
    displayName: 'EuiIconClass',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'SVGAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      type: {
        defaultValue: null,
        description: '`Enum` is any of the named icons listed in the docs, `string` is usually a URL to an SVG file, and `elementType` is any React SVG component',
        name: 'type',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/icon/icon.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'IconType'
        }
      },
      color: {
        defaultValue: null,
        description: "One of EUI's color palette or a valid CSS color value https://developer.mozilla.org/en-US/docs/Web/CSS/color_value.\n" + 'Note that coloring only works if your SVG is removed of fill attributes.',
        name: 'color',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/icon/icon.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      size: {
        defaultValue: null,
        description: 'Note that every size other than `original` assumes the provided SVG sits on a square viewbox.',
        name: 'size',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/icon/icon.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "m" | "l" | "xl" | "original" | "xxl"',
          value: [{
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"l"'
          }, {
            value: '"xl"'
          }, {
            value: '"original"'
          }, {
            value: '"xxl"'
          }]
        }
      },
      title: {
        defaultValue: null,
        description: 'Descriptive title for naming the icon based on its use',
        name: 'title',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/icon/icon.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      titleId: {
        defaultValue: null,
        description: 'A unique identifier for the title element',
        name: 'titleId',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/icon/icon.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      onIconLoad: {
        defaultValue: null,
        description: 'Callback when the icon has been loaded & rendered',
        name: 'onIconLoad',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/icon/icon.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '() => void'
        }
      },
      theme: {
        defaultValue: null,
        description: '',
        name: 'theme',
        parent: {
          fileName: 'app/src/services/theme/hooks.tsx',
          name: 'WithEuiThemeProps'
        },
        declarations: [{
          fileName: 'app/src/services/theme/hooks.tsx',
          name: 'WithEuiThemeProps'
        }],
        required: true,
        type: {
          name: 'UseEuiTheme<{}>'
        }
      }
    },
    extendedInterfaces: ['String', 'CommonProps', 'SVGAttributes', 'AriaAttributes', 'DOMAttributes', 'WithEuiThemeProps', 'Attributes']
  };
} catch (e) {}
try {
  EuiIcon.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/icon/icon.tsx',
    description: '',
    displayName: 'EuiIcon',
    methods: [],
    props: {
      color: {
        defaultValue: null,
        description: "One of EUI's color palette or a valid CSS color value https://developer.mozilla.org/en-US/docs/Web/CSS/color_value.\n" + 'Note that coloring only works if your SVG is removed of fill attributes.',
        name: 'color',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/icon/icon.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'SVGAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      title: {
        defaultValue: null,
        description: 'Descriptive title for naming the icon based on its use',
        name: 'title',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/icon/icon.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@emotion/react/types/css-prop.d.ts',
          name: 'Attributes'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      size: {
        defaultValue: null,
        description: 'Note that every size other than `original` assumes the provided SVG sits on a square viewbox.',
        name: 'size',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/icon/icon.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "m" | "l" | "xl" | "original" | "xxl"',
          value: [{
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"l"'
          }, {
            value: '"xl"'
          }, {
            value: '"original"'
          }, {
            value: '"xxl"'
          }]
        }
      },
      type: {
        defaultValue: null,
        description: '`Enum` is any of the named icons listed in the docs, `string` is usually a URL to an SVG file, and `elementType` is any React SVG component',
        name: 'type',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/icon/icon.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'IconType'
        }
      },
      titleId: {
        defaultValue: null,
        description: 'A unique identifier for the title element',
        name: 'titleId',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/icon/icon.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      onIconLoad: {
        defaultValue: null,
        description: 'Callback when the icon has been loaded & rendered',
        name: 'onIconLoad',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/icon/icon.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '() => void'
        }
      },
      ref: {
        defaultValue: null,
        description: '',
        name: 'ref',
        parent: {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'RefAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'RefAttributes'
        }],
        required: false,
        type: {
          name: 'Ref<unknown>'
        }
      }
    },
    extendedInterfaces: ['String', 'CommonProps', 'SVGAttributes', 'AriaAttributes', 'DOMAttributes', 'WithEuiThemeProps', 'Attributes']
  };
} catch (e) {}